import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface ContactMessage {
  id: string;
  full_name: string;
  email: string;
  subject: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

interface Announcement {
  id: string;
  title: string;
  content: string;
  target_audience: string;
  created_at: string;
  users: {
    full_name: string;
  };
}

interface NotificationItem {
  id: string;
  type: 'contact' | 'announcement';
  title: string;
  preview: string;
  sender: string;
  time: string;
  isRead: boolean;
  data: ContactMessage | Announcement;
}

interface NotificationPanelProps {
  onClose: () => void;
  onUnreadCountChange: (count: number) => void;
}

export default function NotificationPanel({ onClose, onUnreadCountChange }: NotificationPanelProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
  }, [user?.id, user?.role]);

  const fetchNotifications = async () => {
    if (!user) return;

    setLoading(true);
    const items: NotificationItem[] = [];

    try {
      // Fetch contact messages for admin only
      if (user.role === 'admin') {
        const { data: messages } = await supabase
          .from('contact_messages')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(10);

        if (messages) {
          messages.forEach((msg) => {
            items.push({
              id: `contact-${msg.id}`,
              type: 'contact',
              title: msg.subject,
              preview: msg.message.substring(0, 80) + (msg.message.length > 80 ? '...' : ''),
              sender: msg.full_name,
              time: getTimeAgo(msg.created_at),
              isRead: msg.is_read,
              data: msg,
            });
          });
        }
      }

      // Fetch announcements based on role
      let announcementsQuery = supabase
        .from('announcements')
        .select(`
          *,
          users:created_by (
            full_name
          )
        `)
        .order('created_at', { ascending: false })
        .limit(10);

      // Filter announcements by target audience
      if (user.role === 'teacher') {
        announcementsQuery = announcementsQuery.in('target_audience', ['all', 'teachers']);
      } else if (user.role === 'student') {
        announcementsQuery = announcementsQuery.in('target_audience', ['all', 'students']);
      } else if (user.role === 'parent') {
        announcementsQuery = announcementsQuery.in('target_audience', ['all', 'parents']);
      }

      const { data: announcements } = await announcementsQuery;

      if (announcements) {
        announcements.forEach((ann) => {
          items.push({
            id: `announcement-${ann.id}`,
            type: 'announcement',
            title: ann.title,
            preview: ann.content.substring(0, 80) + (ann.content.length > 80 ? '...' : ''),
            sender: ann.users?.full_name || 'System',
            time: getTimeAgo(ann.created_at),
            isRead: true, // Announcements are always marked as read
            data: ann,
          });
        });
      }

      // Sort by time (most recent first)
      items.sort((a, b) => {
        const timeA = new Date((a.data as any).created_at).getTime();
        const timeB = new Date((b.data as any).created_at).getTime();
        return timeB - timeA;
      });

      const finalItems = items.slice(0, 15);
      setNotifications(finalItems);

      // Calculate unread count and notify parent
      const unreadCount = finalItems.filter(item => !item.isRead).length;
      onUnreadCountChange(unreadCount);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeAgo = (timestamp: string): string => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return past.toLocaleDateString();
  };

  const markAsRead = async (notificationId: string) => {
    const notification = notifications.find((n) => n.id === notificationId);
    if (!notification || notification.type !== 'contact' || notification.isRead) return;

    const contactId = (notification.data as ContactMessage).id;

    try {
      await supabase
        .from('contact_messages')
        .update({ is_read: true })
        .eq('id', contactId);

      const updatedNotifications = notifications.map((n) =>
        n.id === notificationId ? { ...n, isRead: true } : n
      );
      setNotifications(updatedNotifications);

      // Update unread count
      const unreadCount = updatedNotifications.filter(item => !item.isRead).length;
      onUnreadCountChange(unreadCount);
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (user?.role !== 'admin') return;

    try {
      await supabase
        .from('contact_messages')
        .update({ is_read: true })
        .eq('is_read', false);

      const updatedNotifications = notifications.map((n) => ({ ...n, isRead: true }));
      setNotifications(updatedNotifications);

      // Update unread count to 0
      onUnreadCountChange(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  return (
    <div className="absolute top-full right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
        {user?.role === 'admin' && notifications.some((n) => !n.isRead) && (
          <button
            onClick={markAllAsRead}
            className="text-sm text-red-600 hover:text-red-700 font-medium whitespace-nowrap"
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Notifications List */}
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <i className="ri-notification-off-line text-3xl text-gray-400"></i>
            </div>
            <p className="text-gray-500 text-sm text-center">No notifications yet</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notification) => (
              <button
                key={notification.id}
                onClick={() => {
                  markAsRead(notification.id);
                  if (notification.type === 'contact') {
                    navigate('/contact-messages');
                    onClose();
                  }
                }}
                className="w-full px-4 py-3 hover:bg-gray-50 transition-colors text-left flex items-start gap-3"
              >
                {/* Icon */}
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    notification.type === 'contact'
                      ? 'bg-blue-100'
                      : 'bg-green-100'
                  }`}
                >
                  <i
                    className={`text-lg ${
                      notification.type === 'contact'
                        ? 'ri-mail-line text-blue-600'
                        : 'ri-megaphone-line text-green-600'
                    }`}
                  ></i>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <h4 className="text-sm font-semibold text-gray-900 truncate">
                      {notification.title}
                    </h4>
                    {!notification.isRead && (
                      <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-1"></span>
                    )}
                  </div>
                  <p className="text-xs text-gray-600 mb-1">
                    From: {notification.sender}
                  </p>
                  <p className="text-xs text-gray-500 line-clamp-2 mb-1">
                    {notification.preview}
                  </p>
                  <p className="text-xs text-gray-400">{notification.time}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
          {user?.role === 'admin' ? (
            <button
              onClick={() => { navigate('/contact-messages'); onClose(); }}
              className="text-sm text-red-600 hover:text-red-700 font-medium whitespace-nowrap"
            >
              View All Messages
            </button>
          ) : (
            <span />
          )}
          <button
            onClick={onClose}
            className="text-sm text-gray-600 hover:text-gray-900 font-medium whitespace-nowrap"
          >
            Close
          </button>
        </div>
      )}
    </div>
  );
}